/**
 * Input Persistence Hook
 *
 * Manages sessionStorage persistence for chat input text and content blocks.
 * Handles conversation switching with state save/restore.
 *
 * @module features/chat/hooks/chat-input/useInputPersistence
 */

import { useEffect, useRef } from "react";
import type { Base64ContentBlock, StorageAudioBlock, ContentBlock } from "../../types/messages";
import {
  getCaptureContentBlocks,
  saveCaptureContentBlocks,
} from "../../services/capture-session-service";

// =============================================================================
// CONSTANTS
// =============================================================================

const STORAGE_KEY_INPUT_PREFIX = "chat-input-text";
const STORAGE_KEY_BLOCKS_PREFIX = "chat-content-blocks";

// =============================================================================
// HELPERS
// =============================================================================

function getInputStorageKey(conversationId: string | undefined): string {
  return conversationId
    ? `${STORAGE_KEY_INPUT_PREFIX}-${conversationId}`
    : STORAGE_KEY_INPUT_PREFIX;
}

function getBlocksStorageKey(conversationId: string | undefined): string {
  return conversationId
    ? `${STORAGE_KEY_BLOCKS_PREFIX}-${conversationId}`
    : STORAGE_KEY_BLOCKS_PREFIX;
}

function getStoredInput(conversationId: string | undefined): string {
  try {
    return sessionStorage.getItem(getInputStorageKey(conversationId)) || "";
  } catch {
    return "";
  }
}

function getStoredBlocks(
  conversationId: string | undefined
): (Base64ContentBlock | StorageAudioBlock)[] {
  try {
    const stored = sessionStorage.getItem(getBlocksStorageKey(conversationId));
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch {
    // Ignore parse errors
  }
  return [];
}

// =============================================================================
// TYPES
// =============================================================================

export interface UseInputPersistenceOptions {
  conversationId: string | undefined;
  captureMode: boolean;
  input: string;
  contentBlocks: ContentBlock[];
  setInput: (value: string) => void;
  setContentBlocks: (blocks: ContentBlock[]) => void;
  onConversationChange?: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for managing chat input persistence across conversation switches.
 *
 * Features:
 * - Saves input text and content blocks to sessionStorage
 * - Restores state when switching conversations
 * - In capture mode: uses separate storage key (capture-content-blocks-{id})
 * - Capture mode content persists after "Rapport opstellen" for re-entry
 * - Handles conversation ID changes
 */
export function useInputPersistence({
  conversationId,
  captureMode,
  input,
  contentBlocks,
  setInput,
  setContentBlocks,
  onConversationChange,
}: UseInputPersistenceOptions): void {
  const prevConversationIdRef = useRef<string | undefined>(undefined);
  const prevCaptureModeRef = useRef<boolean>(false);
  const isInitializedRef = useRef(false);

  // Handle conversationId changes - save current state and restore for new conversation
  useEffect(() => {
    const prevId = prevConversationIdRef.current;

    // Save current input to previous conversation's storage before switching
    if (prevId && prevId !== conversationId) {
      try {
        if (input) {
          sessionStorage.setItem(getInputStorageKey(prevId), input);
        } else {
          sessionStorage.removeItem(getInputStorageKey(prevId));
        }
        if (contentBlocks.length > 0) {
          sessionStorage.setItem(
            getBlocksStorageKey(prevId),
            JSON.stringify(contentBlocks)
          );
        } else {
          sessionStorage.removeItem(getBlocksStorageKey(prevId));
        }
      } catch {
        // Ignore storage errors
      }
    }

    // Restore input for new/current conversation
    if (conversationId && (conversationId !== prevId || !isInitializedRef.current)) {
      if (captureMode) {
        // In capture mode: restore from capture-specific storage
        const captureBlocks = getCaptureContentBlocks(conversationId);
        setInput("");
        setContentBlocks(captureBlocks as ContentBlock[]);
      } else {
        // Normal mode: restore from regular chat storage
        const storedInput = getStoredInput(conversationId);
        const storedBlocks = getStoredBlocks(conversationId);
        setInput(storedInput);
        setContentBlocks(storedBlocks as ContentBlock[]);
      }
      isInitializedRef.current = true;
    }

    // Clear input when no conversation is selected
    if (!conversationId && prevId) {
      setInput("");
      setContentBlocks([]);
    }

    // Notify parent of conversation change
    if (conversationId !== prevId && onConversationChange) {
      onConversationChange();
    }

    prevConversationIdRef.current = conversationId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, captureMode, setContentBlocks]);

  // Restore capture blocks when entering capture mode (same conversation)
  useEffect(() => {
    const wasCapturing = prevCaptureModeRef.current;

    if (captureMode && !wasCapturing && conversationId) {
      // Just entered capture mode - restore capture blocks if any exist
      const captureBlocks = getCaptureContentBlocks(conversationId);
      if (captureBlocks.length > 0) {
        setContentBlocks(captureBlocks as ContentBlock[]);
      }
    }

    prevCaptureModeRef.current = captureMode;
  }, [captureMode, conversationId, setContentBlocks]);

  // Persist input text to sessionStorage (per conversation)
  useEffect(() => {
    if (!conversationId || captureMode) return;
    try {
      const key = getInputStorageKey(conversationId);
      if (input) {
        sessionStorage.setItem(key, input);
      } else {
        sessionStorage.removeItem(key);
      }
    } catch {
      // Ignore storage errors
    }
  }, [input, conversationId, captureMode]);

  // Persist content blocks to sessionStorage
  useEffect(() => {
    if (!conversationId) return;

    if (captureMode) {
      // In capture mode: save to capture-specific storage
      saveCaptureContentBlocks(conversationId, contentBlocks);
    } else {
      // Normal mode: save to regular chat storage
      try {
        const key = getBlocksStorageKey(conversationId);
        if (contentBlocks.length > 0) {
          sessionStorage.setItem(key, JSON.stringify(contentBlocks));
        } else {
          sessionStorage.removeItem(key);
        }
      } catch {
        // Ignore storage errors
      }
    }
  }, [contentBlocks, conversationId, captureMode]);

}

// Export storage key helpers for external use (e.g., clearing on submit)
export { getInputStorageKey, getBlocksStorageKey };
